import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader, handleApiError } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Trash2, RotateCcw, AlertTriangle, Clock, User, 
  Users, Package, Building2, MessageSquare, DollarSign,
  Wrench, Filter, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';

// Ícones por tipo
var TIPO_ICONS = {
  clientes: Users,
  planos: Package,
  filiais: Building2,
  users: User,
  atendimentos: MessageSquare,
  receitas: DollarSign,
  despesas: DollarSign,
  ordens_servico: Wrench
};

var TIPO_CORES = {
  clientes: 'bg-blue-100 text-blue-700',
  planos: 'bg-purple-100 text-purple-700',
  filiais: 'bg-green-100 text-green-700',
  users: 'bg-orange-100 text-orange-700',
  atendimentos: 'bg-cyan-100 text-cyan-700',
  receitas: 'bg-emerald-100 text-emerald-700',
  despesas: 'bg-red-100 text-red-700',
  ordens_servico: 'bg-amber-100 text-amber-700'
};

function ItemLixeira(props) {
  var item = props.item;
  var onRestaurar = props.onRestaurar;
  var onExcluir = props.onExcluir;
  var loading = props.loading;
  
  var IconeTipo = TIPO_ICONS[item.tipo] || Trash2;
  var corTipo = TIPO_CORES[item.tipo] || 'bg-slate-100 text-slate-700';
  
  function formatarData(dataStr) {
    if (!dataStr) return '-';
    try {
      var data = new Date(dataStr);
      return data.toLocaleDateString('pt-BR') + ' ' + data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return dataStr;
    }
  }
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Ícone e Tipo */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={'p-2 rounded-lg ' + corTipo}>
            <IconeTipo className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-800 truncate">{item.nome}</p>
            <p className="text-sm text-slate-500">
              <span className={'px-2 py-0.5 rounded text-xs ' + corTipo}>{item.tipo_nome}</span>
            </p>
          </div>
        </div>
        
        {/* Info de exclusão */}
        <div className="flex flex-col sm:items-end text-sm text-slate-500 gap-1">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{formatarData(item.deleted_at)}</span>
          </div>
          {item.deleted_by_name && (
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              <span>{item.deleted_by_name}</span>
            </div>
          )}
          <div className={'flex items-center gap-1 font-medium ' + (item.dias_restantes <= 7 ? 'text-red-500' : item.dias_restantes <= 30 ? 'text-orange-500' : 'text-slate-600')}>
            <AlertTriangle className="w-3 h-3" />
            <span>{item.dias_restantes} dias restantes</span>
          </div>
        </div>
        
        {/* Dados resumo */}
        {item.dados_resumo && (
          <div className="hidden lg:block text-xs text-slate-500 border-l pl-3 ml-3">
            {item.tipo === 'clientes' && item.dados_resumo.telefone && (
              <p>Tel: {item.dados_resumo.telefone}</p>
            )}
            {item.tipo === 'clientes' && item.dados_resumo.plano && (
              <p>Plano: {item.dados_resumo.plano}</p>
            )}
            {(item.tipo === 'receitas' || item.tipo === 'despesas') && item.dados_resumo.valor && (
              <p>R$ {parseFloat(item.dados_resumo.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            )}
            {item.tipo === 'planos' && item.dados_resumo.valor && (
              <p>R$ {parseFloat(item.dados_resumo.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            )}
          </div>
        )}
        
        {/* Ações */}
        <div className="flex gap-2 mt-2 sm:mt-0">
          <Button
            onClick={function() { onRestaurar(item); }}
            disabled={loading}
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <RotateCcw className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Restaurar</span>
          </Button>
          <Button
            onClick={function() { onExcluir(item); }}
            disabled={loading}
            size="sm"
            variant="outline"
            className="text-red-500 border-red-300 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Excluir</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function LixeiraPage() {
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [actionLoading, setActionLoading] = useState(false);
  var [filtroTipo, setFiltroTipo] = useState('');
  var [totalPorTipo, setTotalPorTipo] = useState({});
  var [pagination, setPagination] = useState({ page: 1, limit: 50, total_pages: 1 });
  var [retencaoDias, setRetencaoDias] = useState(90);
  
  useEffect(function() {
    carregarLixeira();
  }, [filtroTipo, pagination.page]);
  
  function carregarLixeira() {
    setLoading(true);
    var params = new URLSearchParams();
    params.append('page', pagination.page);
    params.append('limit', 50);
    if (filtroTipo) params.append('tipo', filtroTipo);
    
    axios.get(API + '/lixeira?' + params.toString(), { headers: getAuthHeader() })
      .then(function(response) {
        setItems(response.data.items || []);
        setTotalPorTipo(response.data.total_por_tipo || {});
        setPagination(response.data.pagination || { page: 1, limit: 50, total_pages: 1 });
        setRetencaoDias(response.data.retencao_dias || 90);
      })
      .catch(function(error) {
        handleApiError(error);
        toast.error('Erro ao carregar lixeira');
      })
      .finally(function() {
        setLoading(false);
      });
  }
  
  function restaurarItem(item) {
    if (!window.confirm('Tem certeza que deseja restaurar "' + item.nome + '"?')) return;
    
    setActionLoading(true);
    axios.post(API + '/lixeira/restaurar/' + item.tipo + '/' + item.id, {}, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success(response.data.message);
        carregarLixeira();
      })
      .catch(function(error) {
        handleApiError(error);
        toast.error('Erro ao restaurar item');
      })
      .finally(function() {
        setActionLoading(false);
      });
  }
  
  function excluirPermanente(item) {
    if (!window.confirm('⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!\n\nTem certeza que deseja excluir permanentemente "' + item.nome + '"?')) return;
    
    setActionLoading(true);
    axios.delete(API + '/lixeira/excluir-permanente/' + item.tipo + '/' + item.id, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success(response.data.message);
        carregarLixeira();
      })
      .catch(function(error) {
        handleApiError(error);
        toast.error('Erro ao excluir item');
      })
      .finally(function() {
        setActionLoading(false);
      });
  }
  
  function esvaziarLixeira() {
    var msg = filtroTipo 
      ? 'Tem certeza que deseja excluir permanentemente TODOS os itens de "' + (TIPO_ICONS[filtroTipo] ? filtroTipo : '') + '" da lixeira?'
      : 'Tem certeza que deseja excluir permanentemente TODOS os itens da lixeira?';
    
    if (!window.confirm('⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!\n\n' + msg)) return;
    if (!window.confirm('ÚLTIMA CONFIRMAÇÃO: Todos os dados serão perdidos para sempre. Continuar?')) return;
    
    setActionLoading(true);
    var url = API + '/lixeira/esvaziar';
    if (filtroTipo) url += '?tipo=' + filtroTipo;
    
    axios.post(url, {}, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success(response.data.message);
        carregarLixeira();
      })
      .catch(function(error) {
        handleApiError(error);
        toast.error('Erro ao esvaziar lixeira');
      })
      .finally(function() {
        setActionLoading(false);
      });
  }
  
  var totalItems = Object.values(totalPorTipo).reduce(function(a, b) { return a + b; }, 0);
  
  return (
    <div className="space-y-6" data-testid="lixeira-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Trash2 className="w-8 h-8 text-slate-600" />
            LIXEIRA
          </h1>
          <p className="text-slate-500 text-sm">
            {totalItems} item(ns) • Retenção: {retencaoDias} dias
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={carregarLixeira} disabled={loading} variant="outline" size="sm">
            <RefreshCw className={loading ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
          </Button>
          {totalItems > 0 && (
            <Button 
              onClick={esvaziarLixeira} 
              disabled={actionLoading}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Esvaziar</span>
            </Button>
          )}
        </div>
      </div>
      
      {/* Filtros por Tipo */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={function() { setFiltroTipo(''); setPagination({...pagination, page: 1}); }}
              size="sm"
              variant={filtroTipo === '' ? 'default' : 'outline'}
              className={filtroTipo === '' ? 'bg-slate-800' : ''}
            >
              Todos ({totalItems})
            </Button>
            {Object.entries(TIPO_ICONS).map(function(entry) {
              var tipo = entry[0];
              var Icon = entry[1];
              var count = totalPorTipo[tipo] || 0;
              if (count === 0) return null;
              return (
                <Button
                  key={tipo}
                  onClick={function() { setFiltroTipo(tipo); setPagination({...pagination, page: 1}); }}
                  size="sm"
                  variant={filtroTipo === tipo ? 'default' : 'outline'}
                  className={filtroTipo === tipo ? 'bg-slate-800' : ''}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {tipo.charAt(0).toUpperCase() + tipo.slice(1)} ({count})
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Aviso */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Itens na lixeira serão excluídos permanentemente após {retencaoDias} dias.</p>
              <p className="text-amber-700">Apenas Super Admin pode restaurar ou excluir permanentemente.</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Lista de Itens */}
      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <Trash2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">A lixeira está vazia</p>
              <p className="text-slate-400 text-sm">Itens excluídos aparecerão aqui</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map(function(item) {
                return (
                  <ItemLixeira
                    key={item.tipo + '-' + item.id}
                    item={item}
                    onRestaurar={restaurarItem}
                    onExcluir={excluirPermanente}
                    loading={actionLoading}
                  />
                );
              })}
            </div>
          )}
          
          {/* Paginação */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
              <Button
                onClick={function() { setPagination({...pagination, page: pagination.page - 1}); }}
                disabled={pagination.page <= 1 || loading}
                size="sm"
                variant="outline"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 px-2">
                {pagination.page} / {pagination.total_pages}
              </span>
              <Button
                onClick={function() { setPagination({...pagination, page: pagination.page + 1}); }}
                disabled={pagination.page >= pagination.total_pages || loading}
                size="sm"
                variant="outline"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
